/**
 * Hierarchy Visualization Component
 * 
 * Displays items in a hierarchical tree structure based on:
 * 1. Altitude levels (outcome → initiative → epic → feature → story → task)
 * 2. Parent/child relationships
 * 
 * Features:
 * - Visual tree structure with connecting lines
 * - Expandable/collapsible nodes
 * - Altitude-based color coding
 * - Click to select/navigate to item
 */

import { useState, useMemo } from 'react';
import { 
  ChevronRight, 
  ChevronDown, 
  Circle, 
  Layers,
  GitBranch,
  Target,
  Rocket,
  Box,
  BookOpen,
  Wrench,
} from 'lucide-react';
import type { ItemAltitude } from '../../types/prioritization/itemLifecycle';
import { useAltitudeText } from '../../hooks/prioritization/useAltitudeText';

// ============================================================================
// Types
// ============================================================================

export interface HierarchyItem {
  id: string;
  title: string;
  altitude?: ItemAltitude;
  parentItemId?: string;
  childItemIds?: string[];
  description?: string;
  status?: string;
}

export interface HierarchyNode extends HierarchyItem {
  children: HierarchyNode[];
  depth: number;
}

interface HierarchyViewProps {
  items: HierarchyItem[];
  onItemClick?: (itemId: string) => void;
  selectedItemId?: string;
  showOrphans?: boolean;
  groupByAltitude?: boolean;
  maxDepth?: number;
}

// ============================================================================
// Altitude Configuration
// ============================================================================

const ALTITUDE_CONFIG: Record<ItemAltitude, {
  icon: typeof Target;
  color: string;
  bgColor: string;
  borderColor: string;
}> = {
  outcome: {
    icon: Target,
    color: 'text-[var(--text-sec)]',
    bgColor: 'bg-[var(--slate-dim)]',
    borderColor: 'border-[var(--slate)]',
  },
  initiative: {
    icon: Rocket,
    color: 'text-[var(--text-sec)]',
    bgColor: 'bg-[var(--slate-dim)]',
    borderColor: 'border-[var(--slate)]',
  },
  epic: {
    icon: Layers,
    color: 'text-[var(--text-sec)]',
    bgColor: 'bg-[var(--slate-dim)]',
    borderColor: 'border-[var(--slate)]',
  },
  feature: {
    icon: Box,
    color: 'text-[var(--green)]',
    bgColor: 'bg-[var(--green-dim)]',
    borderColor: 'border-[var(--green)]',
  },
  story: {
    icon: BookOpen,
    color: 'text-[var(--mustard)]',
    bgColor: 'bg-[var(--mustard-dim)]',
    borderColor: 'border-[var(--mustard)]',
  },
  task: {
    icon: Wrench,
    color: 'text-[var(--text-sec)]',
    bgColor: 'bg-[var(--surface)]',
    borderColor: 'border-[var(--rule)]',
  },
};

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Build a hierarchical tree from flat list of items
 */
function buildHierarchy(items: HierarchyItem[]): {
  roots: HierarchyNode[];
  orphans: HierarchyNode[];
} {
  const itemMap = new Map<string, HierarchyItem>();
  const childrenMap = new Map<string, string[]>();
  
  // Index items
  for (const item of items) {
    itemMap.set(item.id, item);
    if (!childrenMap.has(item.id)) {
      childrenMap.set(item.id, []);
    }
    
    if (item.parentItemId) {
      const siblings = childrenMap.get(item.parentItemId) || [];
      siblings.push(item.id);
      childrenMap.set(item.parentItemId, siblings);
    }
  }
  
  // Build nodes recursively
  function buildNode(item: HierarchyItem, depth: number): HierarchyNode {
    const childIds = childrenMap.get(item.id) || [];
    const children = childIds
      .map(id => itemMap.get(id))
      .filter((i): i is HierarchyItem => i !== undefined)
      .map(child => buildNode(child, depth + 1));
    
    return {
      ...item,
      children,
      depth,
    };
  }
  
  // Find roots (items with no parent or parent not in list)
  const roots: HierarchyNode[] = [];
  const orphans: HierarchyNode[] = [];
  
  for (const item of items) {
    if (!item.parentItemId || !itemMap.has(item.parentItemId)) {
      const node = buildNode(item, 0);
      
      // Check if it's a true root (has children) or an orphan
      if (node.children.length > 0 || !item.parentItemId) {
        roots.push(node);
      } else {
        orphans.push(node);
      }
    }
  }
  
  return { roots, orphans };
}

/**
 * Group items by altitude level
 */
function groupByAltitudeLevel(items: HierarchyItem[]): Map<ItemAltitude | 'unset', HierarchyItem[]> {
  const groups = new Map<ItemAltitude | 'unset', HierarchyItem[]>();
  
  for (const item of items) {
    const key = item.altitude || 'unset';
    const group = groups.get(key) || [];
    group.push(item);
    groups.set(key, group);
  }
  
  return groups;
}

// ============================================================================
// Tree Node Component
// ============================================================================

interface TreeNodeProps {
  node: HierarchyNode;
  onItemClick?: (itemId: string) => void;
  selectedItemId?: string;
  defaultExpanded?: boolean;
  maxDepth?: number;
}

function TreeNode({
  node,
  onItemClick,
  selectedItemId,
  defaultExpanded = true,
  maxDepth = 10,
}: TreeNodeProps) {
  const altitudeText = useAltitudeText();
  const [expanded, setExpanded] = useState(defaultExpanded && node.depth < 2);
  
  const hasChildren = node.children.length > 0;
  const isSelected = node.id === selectedItemId;
  const config = node.altitude ? ALTITUDE_CONFIG[node.altitude] : null;
  const Icon = config?.icon || Circle;
  
  const canExpand = hasChildren && node.depth < maxDepth;
  
  return (
    <div className="select-none">
      {/* Node */}
      <div
        className={`flex items-center gap-2 py-1.5 px-2  cursor-pointer transition-colors ${
          isSelected
            ? 'bg-primary-100 border border-primary-300'
            : 'hover:bg-[var(--surface)]'
        }`}
        onClick={() => onItemClick?.(node.id)}
        style={{ marginLeft: `${node.depth * 20}px` }}
      >
        {/* Expand/Collapse Toggle */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (canExpand) setExpanded(!expanded);
          }}
          className={`p-0.5 rounded ${
            canExpand ? 'text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)]' : 'text-transparent'
          }`}
          disabled={!canExpand}
        >
          {expanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </button>
        
        {/* Altitude Icon */}
        <div className={`p-1 rounded ${config?.bgColor || 'bg-[var(--hover-tint)]'}`}>
          <Icon className={`h-3.5 w-3.5 ${config?.color || 'text-[var(--text-ter)]'}`} />
        </div>
        
        {/* Title */}
        <span className={`text-sm font-medium flex-1 truncate ${
          isSelected ? 'text-primary-900' : 'text-[var(--text-sec)]'
        }`}>
          {node.title}
        </span>
        
        {/* Altitude Badge */}
        {node.altitude && (
          <span className={`text-xs px-1.5 py-0.5 rounded ${
            config?.bgColor || 'bg-[var(--hover-tint)]'
          } ${config?.color || 'text-[var(--text-sec)]'}`}>
            {altitudeText.format(node.altitude)}
          </span>
        )}
        
        {/* Children Count */}
        {hasChildren && (
          <span className="text-xs text-[var(--text-ter)]">
            {node.children.length}
          </span>
        )}
      </div>
      
      {/* Children */}
      {expanded && hasChildren && (
        <div className="relative">
          {/* Connecting Line */}
          <div
            className="absolute left-0 top-0 bottom-0 w-px bg-[var(--hover-tint)]"
            style={{ marginLeft: `${(node.depth + 1) * 20 + 8}px` }}
          />
          
          {node.children.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              onItemClick={onItemClick}
              selectedItemId={selectedItemId}
              defaultExpanded={defaultExpanded}
              maxDepth={maxDepth}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function HierarchyView({
  items,
  onItemClick,
  selectedItemId,
  showOrphans = true,
  groupByAltitude = false,
  maxDepth = 10,
}: HierarchyViewProps) {
  const altitudeText = useAltitudeText();
  const { roots, orphans } = useMemo(() => buildHierarchy(items), [items]);
  const altitudeGroups = useMemo(() => 
    groupByAltitude ? groupByAltitudeLevel(items) : null,
    [items, groupByAltitude]
  );
  
  // If grouping by altitude, show flat grouped view
  if (groupByAltitude && altitudeGroups) {
    const orderedAltitudes: (ItemAltitude | 'unset')[] = [
      'outcome', 'initiative', 'epic', 'feature', 'story', 'task', 'unset'
    ];
    
    return (
      <div className="space-y-4">
        {orderedAltitudes.map((altitude) => {
          const group = altitudeGroups.get(altitude);
          if (!group || group.length === 0) return null;
          
          const config = altitude !== 'unset' ? ALTITUDE_CONFIG[altitude] : null;
          const Icon = config?.icon || Circle;
          
          return (
            <div key={altitude} className="space-y-1">
              <div className={`flex items-center gap-2 px-2 py-1.5  ${
                config?.bgColor || 'bg-[var(--hover-tint)]'
              } ${config?.borderColor || 'border-[var(--rule)]'} border`}>
                <Icon className={`h-4 w-4 ${config?.color || 'text-[var(--text-ter)]'}`} />
                <span className={`font-medium text-sm ${config?.color || 'text-[var(--text-sec)]'}`}>
                  {altitude !== 'unset' ? altitudeText.format(altitude) : 'Unset Altitude'}
                </span>
                <span className="text-xs text-[var(--text-ter)] ml-auto">
                  {group.length} item{group.length !== 1 ? 's' : ''}
                </span>
              </div>
              
              <div className="pl-4 space-y-0.5">
                {group.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => onItemClick?.(item.id)}
                    className={`flex items-center gap-2 py-1.5 px-2  cursor-pointer transition-colors ${
                      item.id === selectedItemId
                        ? 'bg-primary-100 border border-primary-300'
                        : 'hover:bg-[var(--surface)]'
                    }`}
                  >
                    <Circle className="h-2 w-2 text-[var(--text-ter)]" />
                    <span className={`text-sm flex-1 truncate ${
                      item.id === selectedItemId ? 'text-primary-900 font-medium' : 'text-[var(--text-sec)]'
                    }`}>
                      {item.title}
                    </span>
                    {item.parentItemId && (
                      <GitBranch className="h-3 w-3 text-[var(--text-ter)]" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  }
  
  // Default tree view
  return (
    <div className="space-y-4">
      {/* Hierarchical Roots */}
      {roots.length > 0 && (
        <div className="space-y-0.5">
          {roots.map((node) => (
            <TreeNode
              key={node.id}
              node={node}
              onItemClick={onItemClick}
              selectedItemId={selectedItemId}
              maxDepth={maxDepth}
            />
          ))}
        </div>
      )}
      
      {/* Orphan Items (with parent not in list) */}
      {showOrphans && orphans.length > 0 && (
        <div className="space-y-1">
          <div className="flex items-center gap-2 px-2 py-1.5 text-[var(--text-ter)] text-sm">
            <Circle className="h-3 w-3" />
            <span>Standalone Items</span>
            <span className="text-xs text-[var(--text-ter)]">({orphans.length})</span>
          </div>
          <div className="space-y-0.5 pl-2">
            {orphans.map((node) => (
              <TreeNode
                key={node.id}
                node={node}
                onItemClick={onItemClick}
                selectedItemId={selectedItemId}
                maxDepth={maxDepth}
              />
            ))}
          </div>
        </div>
      )}
      
      {/* Empty State */}
      {roots.length === 0 && orphans.length === 0 && (
        <div className="text-center py-8 text-[var(--text-ter)]">
          <Layers className="h-8 w-8 mx-auto mb-2 text-[var(--text-ter)]" />
          <p>No items to display</p>
          <p className="text-sm">Add items to see the hierarchy</p>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Compact Hierarchy Badge (for inline display)
// ============================================================================

interface HierarchyBadgeProps {
  item: HierarchyItem;
  allItems: HierarchyItem[];
  showParent?: boolean;
  showChildren?: boolean;
}

export function HierarchyBadge({
  item,
  allItems,
  showParent = true,
  showChildren = true,
}: HierarchyBadgeProps) {
  const parent = showParent && item.parentItemId
    ? allItems.find(i => i.id === item.parentItemId)
    : null;
    
  const childCount = showChildren
    ? allItems.filter(i => i.parentItemId === item.id).length
    : 0;
  
  if (!parent && childCount === 0) return null;
  
  return (
    <div className="flex items-center gap-2 text-xs text-[var(--text-ter)]">
      {parent && (
        <span className="flex items-center gap-1">
          <GitBranch className="h-3 w-3 rotate-180" />
          <span className="truncate max-w-24">{parent.title}</span>
        </span>
      )}
      {childCount > 0 && (
        <span className="flex items-center gap-1">
          <GitBranch className="h-3 w-3" />
          <span>{childCount} child{childCount !== 1 ? 'ren' : ''}</span>
        </span>
      )}
    </div>
  );
}

export default HierarchyView;
